import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface ProximityTextProps {
    text: string
    className?: string
    minWidth?: number
    maxWidth?: number
    minWeight?: number
    maxWeight?: number
    radius?: number
}

const settings = (width: number, weight: number): string => `'wdth' ${width.toFixed(1)}, 'wght' ${weight.toFixed(0)}`

// Every letter widens and thickens as the cursor comes near it, using the
// display face's width and weight axes. Touch screens and reduced motion keep
// the resting shape.
export const ProximityText: FC<ProximityTextProps> = ({
    text,
    className,
    minWidth = 72,
    maxWidth = 125,
    minWeight = 600,
    maxWeight = 850,
    radius = 240
}): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const root = ref.current

        if (!root) return

        const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (!fine || reduce) return

        const letters = Array.from(root.querySelectorAll<HTMLElement>('[data-letter]'))
        const amounts = letters.map(() => 0)
        let pointerX = -9999
        let pointerY = -9999
        let frame = 0

        const tick = () => {
            frame = 0

            const rects = letters.map(letter => letter.getBoundingClientRect())
            let moving = false

            letters.forEach((letter, index) => {
                const rect = rects[index]
                const dx = rect.left + rect.width / 2 - pointerX
                const dy = rect.top + rect.height / 2 - pointerY
                const target = Math.max(0, 1 - Math.sqrt(dx * dx + dy * dy) / radius)
                const next = amounts[index] + (target - amounts[index]) * 0.16

                if (Math.abs(next - amounts[index]) > 0.001) moving = true

                amounts[index] = next
                letter.style.fontVariationSettings = settings(minWidth + (maxWidth - minWidth) * next, minWeight + (maxWeight - minWeight) * next)
            })

            if (moving) frame = requestAnimationFrame(tick)
        }

        const onPointer = (event: PointerEvent) => {
            pointerX = event.clientX
            pointerY = event.clientY

            if (!frame) frame = requestAnimationFrame(tick)
        }

        window.addEventListener('pointermove', onPointer, { passive: true })

        return () => {
            window.removeEventListener('pointermove', onPointer)
            cancelAnimationFrame(frame)
        }
    }, [text, minWidth, maxWidth, minWeight, maxWeight, radius])

    return (
        <span ref={ref} className={cn('relative inline-block whitespace-nowrap', className)}>
            <span className='sr-only'>
                {text}
            </span>
            {Array.from(text).map((letter, index) => (
                <span
                    key={`${letter}-${index}`}
                    aria-hidden
                    data-letter
                    className='inline-block'
                    style={{ fontVariationSettings: settings(minWidth, minWeight) }}
                >
                    {letter === ' ' ? ' ' : letter}
                </span>
            ))}
        </span>
    )
}
